<section id="account-statements" class="tw-w-full" data-cy="statements-screen">
  <form [formGroup]="getStatementsForm" class="statements-form">
    <!-- Date From -->
    <mat-form-field appearance="outline" class="tw-w-full">
      <mat-label data-cy="date-from-txt">{{ 'account.statements.datefrom' | translate: 'Date From' }}</mat-label>
      <input
        matInput
        readonly
        [matDatepicker]="fromPicker"
        [max]="maxDate"
        formControlName="dateFrom"
        (click)="fromPicker.open()"
        data-cy="date-from-input"
      >
      <mat-datepicker-toggle matIconSuffix [for]="fromPicker" data-cy="date-from-cal"/>
      <!-- Align the calendar popup to the input's right edge -->
      <mat-datepicker #fromPicker xPosition="end"/>
    </mat-form-field>

    <!-- Date To -->
    <mat-form-field appearance="outline" class="tw-w-full">
      <mat-label data-cy="date-to-txt">{{ 'account.statements.dateto' | translate: 'Date To' }}</mat-label>
      <input
        matInput
        readonly
        [matDatepicker]="toPicker"
        [max]="maxDate"
        formControlName="dateTo"
        (click)="toPicker.open()"
        data-cy="date-to-input"
      >
      <mat-datepicker-toggle matIconSuffix [for]="toPicker" data-cy="date-to-cal"/>
      <!-- Align the calendar popup to the input's right edge -->
      <mat-datepicker #toPicker xPosition="end"/>
    </mat-form-field>

    <!-- Track -->
    <mat-form-field appearance="outline" class="tw-w-full">
      <mat-label data-cy="track-txt">{{ 'generic.track' | translate: 'Track' }}</mat-label>
      <mat-select formControlName="track" data-cy="track-dropdown">
        @for (track of tracks; track trackByEventId($index, track)) {
          <mat-option [value]="track.EventId">{{ track.EventName }}</mat-option>
        }
      </mat-select>
      @if (formVal['track']?.value) {
        <button
          matSuffix
          mat-icon-button
          type="button"
          attr.aria-label="{{ 'ui.account.clear.track' | translate: 'Clear track' }}"
          (click)="clearControl('track'); $event.stopPropagation()"
          data-cy="track-x-icon"
        >
          <mat-icon>close</mat-icon>
        </button>
      }
    </mat-form-field>

    <!-- Pool -->
    <mat-form-field appearance="outline" class="tw-w-full">
      <mat-label data-cy="pool-txt">{{ 'generic.pool' | translate: 'Pool' }}</mat-label>
      <mat-select formControlName="pool" data-cy="pool-dropdown">
        @for (poolId of poolList; track trackByPool($index, poolId)) {
          <mat-option [value]="poolId">{{ poolId }}</mat-option>
        }
      </mat-select>
      @if (formVal['pool']?.value) {
        <button
          matSuffix
          mat-icon-button
          type="button"
          attr.aria-label="{{ 'ui.account.clear.pool' | translate: 'Clear pool' }}"
          (click)="clearControl('pool'); $event.stopPropagation()"
        >
          <mat-icon>close</mat-icon>
        </button>
      }
    </mat-form-field>

    <button
      mat-flat-button
      type="button"
      (click)="searchStatements()"
      [disabled]="getStatementsForm.invalid"
      [ngClass]="{ 'active': getStatementsForm.valid }"
      class="search-statements tw-w-full"
      data-cy="search-btn"
    >
      {{ 'generic.search' | translate: 'Search' }}
    </button>
  </form>

  <!-- Totals -->
  <div class="tw-flex tw-flex-row tw-w-full tw-mt-[30px] totals">
    <div class="tw-flex tw-flex-col tw-w-6/12 total-label">
      <div><span class="tw-py-[2px]" data-cy="total-deposit-txt">{{ 'account.statements.totaldeposit' | translate: 'Total Deposit:' }}</span></div>
      <div><span class="tw-py-[2px]" data-cy="total-withdraw-txt">{{ 'ui.account.total.withdraw' | translate: 'Total Withdraw:' }}</span></div>
      <div><span class="tw-py-[2px]" data-cy="total-bet-txt">{{ 'account.statements.totalbet' | translate: 'Total Bet:' }}</span></div>
      <div><span class="tw-py-[2px]" data-cy="total-won-txt">{{ 'account.statements.totalwon' | translate: 'Total Won:' }}</span></div>
      <div><span class="tw-py-[2px]" data-cy="total-pl-txt">{{ 'account.statements.profitloss' | translate: 'Total Profit/Loss:' }}</span></div>
    </div>
    <div class="tw-flex tw-flex-col tw-w-6/12 total-value">
      <div>
        <span class="tw-py-[2px]" data-cy="statements-total-deposit">
          {{
            (statements?.response?.Totals?.TotalDeposit | numberWithCommas) === '0'
              ? '$0.00'
              : '$' + (statements?.response?.Totals?.TotalDeposit | numberWithCommas)
          }}
        </span>
      </div>
      <div>
        <span class="tw-py-[2px]" data-cy="statements-total-withdrawal">
          {{
            (statements?.response?.Totals?.TotalWithdrawal | numberWithCommas) === '0'
              ? '$0.00'
              : '$' + (statements?.response?.Totals?.TotalWithdrawal | numberWithCommas)
          }}
        </span>
      </div>
      <div>
        <span class="tw-py-[2px]" data-cy="statements-total-bet">
          {{
            (statements?.response?.Totals?.TotalBet | numberWithCommas) === '0'
              ? '$0.00'
              : '$' + (statements?.response?.Totals?.TotalBet | numberWithCommas)
          }}
        </span>
      </div>
      <div>
        <span class="tw-py-[2px]" data-cy="statements-total-won">
          {{
            (statements?.response?.Totals?.TotalWon | numberWithCommas) === '0'
              ? '$0.00'
              : '$' + (statements?.response?.Totals?.TotalWon | numberWithCommas)
          }}
        </span>
      </div>
      <div>
        <span class="tw-py-[2px]" data-cy="statements-total-pl">
          {{
            statements?.response?.Totals?.TotalBet && statements?.response?.Totals?.TotalWon
              ? (statements?.response?.Totals?.TotalBet && statements?.response?.Totals?.TotalWon === 0)
                ? '$0.00'
                : '$' + ((statements?.response?.Totals?.TotalWon || 0) - (statements?.response?.Totals?.TotalBet || 0) | numberWithCommas)
              : '$0.00'
          }}
        </span>
      </div>
    </div>
  </div>

  <!-- Results — table styled to match the Bet Summary table -->
  <div class="tw-mt-[20px] results">
    @if (statements?.response?.AccountReport?.length) {
      <table class="statements-table tw-w-full" data-cy="statements-table">
        <thead>
          <tr>
            <th data-cy="col-date">{{ 'generic.date' | translate: 'Date' }}</th>
            <th data-cy="col-event">{{ 'generic.eventname' | translate: 'Event Name' }}</th>
            <th data-cy="col-race">{{ 'generic.race' | translate: 'Race' }}</th>
            <th data-cy="col-pool">{{ 'generic.pool' | translate: 'Pool' }}</th>
            <th data-cy="col-runners">{{ 'Runners' | translate: 'Runners' }}</th>
            <th data-cy="col-status">{{ 'Status' | translate: 'Status' }}</th>
            <th data-cy="col-amount">{{ 'Amount' | translate: 'Amount' }}</th>
            <th data-cy="col-credit">{{ 'Credit' | translate: 'Credit' }}</th>
            <th data-cy="col-debit">{{ 'Debit' | translate: 'Debit' }}</th>
            <th data-cy="col-pl">P/L</th>
          </tr>
        </thead>
        <tbody>
          @for (item of statements?.response?.AccountReport; track $index) {
            <tr [attr.data-cy]="'statements-row-' + $index">
              <td [attr.data-cy]="'item-date-' + $index">{{ item | statement: 'date' }}</td>
              <td [attr.data-cy]="'item-event-' + $index">{{ item?.EventName | titlecase }}</td>
              <td [attr.data-cy]="'item-race-' + $index">{{ item?.RaceId }}</td>
              <td [attr.data-cy]="'item-pool-' + $index">{{ item | statement: 'pool' }}</td>
              <td [attr.data-cy]="'item-runners-' + $index">{{ item?.Runners }}</td>
              <td [attr.data-cy]="'item-status-' + $index">{{ item | statement: 'status' | titlecase }}</td>
              <td [attr.data-cy]="'item-amount-' + $index">
                {{
                  ((item?.TicketAmount | numberWithCommas) === '0')
                    ? '$0.00'
                    : '$' + (item?.TicketAmount | numberWithCommas)
                }}
              </td>
              <td [attr.data-cy]="'item-credit-' + $index">{{ item | statement: 'credit' }}</td>
              <td [attr.data-cy]="'item-debit-' + $index">{{ item | statement: 'debit' }}</td>
              <td [attr.data-cy]="'item-pl-' + $index">{{ item | statement: 'pl' }}</td>
            </tr>
          }
        </tbody>
      </table>
    } @else {
      <span data-cy="statement_no_records">{{ 'account.statements.norecord' | translate: 'No Records Found' }}</span>
    }
  </div>
</section>
